/**
 * The report spine.
 *
 * Every section has a stable concept key and a display label. The key never
 * changes and is what answers, QA comments, deviations and mined content are
 * attached to. The label is what a doctor sees and may rename. That separation
 * is the reason a doctor can call a section "Current Complaints" while another
 * calls it "Current Symptoms" without the two becoming different sections.
 *
 * Provenance is recorded per section:
 *
 *   observed  read directly from a Beam template or a finished report
 *   derived   inferred from the deviation analysis across 28 doctor overlays
 *   open      a placeholder. Nothing in the library evidences it yet.
 *
 * The IME spine below is `observed`: it is the section order of
 * `IME - Beam base template (QA training version).docx`, including the QA
 * guidance the template carries in-line.
 */
/** The matter header block. Fields, not prose, and mostly filled from the file. */
export const MATTER_FIELDS = [
    { key: 'claim_number', label: 'Claim Number' },
    { key: 'employee', label: 'Employee' },
    { key: 'date_of_birth', label: 'Date of Birth' },
    { key: 'employer', label: 'Employer' },
    { key: 'occupation', label: 'Occupation' },
    { key: 'complaint', label: 'Complaint' },
    { key: 'date_of_injury', label: 'Date of Injury' },
    { key: 'assessment_location', label: 'Location of Clinical Assessment' },
    { key: 'assessment_date', label: 'Date of Clinical Assessment' },
];
const DECLARATION_TEXT = 'I certify that I have read the Expert Witness Code of Conduct, contained in the ' +
    'Federal Court Rules. I agree to be bound by the above Code of Conduct.\n\n' +
    'The following report has been prepared in accordance with that Code.';
const IMPAIRMENT_TRAINING_TEXT = 'I also certify that I have successfully completed training in the evaluation of ' +
    'permanent impairment with respect to the WorkCover Guides.';
const CLOSING_TEXT = 'Otherwise, I trust that this information has been of assistance. If you have any ' +
    'further comments or questions, or you would like to discuss any matter arising ' +
    'from this report, please do not hesitate to contact me.';
/**
 * The IME spine, in template order.
 *
 * The Schedule of Documents headings are sections in their own right rather
 * than formatting, because the deviation analysis treats them that way: a
 * doctor who does not use an Imaging heading is recorded as missing that
 * section, and the same key carries the sorted casefile's imaging documents
 * straight into the schedule.
 */
export const IME_SPINE = [
    {
        key: 'matter_header',
        label: 'Matter details',
        kind: 'fields',
        provenance: 'observed',
        required: true,
        guidance: 'Check every field on this page against the referral documents.',
    },
    {
        key: 'opening',
        label: 'Opening',
        kind: 'prose',
        provenance: 'observed',
        required: true,
        guidance: 'Thank the referrer, name the assessment type and the person assessed. ' +
            'For a telehealth assessment, state where each party was and that the ' +
            'assessment was not negatively affected.',
    },
    {
        key: 'purpose',
        label: 'Purpose of assessment',
        kind: 'prose',
        provenance: 'observed',
        required: true,
        guidance: 'State the injury under review and that questions in the referral correspondence are answered.',
    },
    {
        key: 'accompanying_documentation',
        label: 'Accompanying Documentation',
        kind: 'list',
        provenance: 'observed',
        required: true,
        guidance: 'The documents reviewed and considered. Signed consent, referral correspondence ' +
            'with its date, the schedule of documents, and any imaging listed under Investigations.',
    },
    {
        key: 'declaration',
        label: 'Declaration',
        kind: 'boilerplate',
        provenance: 'observed',
        required: true,
        boilerplate: DECLARATION_TEXT,
    },
    {
        key: 'impairment_training_declaration',
        label: 'Impairment training declaration',
        kind: 'boilerplate',
        provenance: 'observed',
        boilerplate: IMPAIRMENT_TRAINING_TEXT,
        guidance: 'Included where the doctor is an approved impairment assessor.',
    },
    { key: 'history', label: 'History', kind: 'prose', provenance: 'observed', required: true, allowsNotes: true },
    { key: 'progress', label: 'Progress', kind: 'prose', provenance: 'observed', allowsNotes: true },
    { key: 'current_symptoms', label: 'Current Symptoms', kind: 'prose', provenance: 'observed', required: true, allowsNotes: true },
    { key: 'current_medical_treatment', label: 'Current Medical Treatment', kind: 'prose', provenance: 'observed', required: true, allowsNotes: true },
    { key: 'current_activities', label: 'Current Activities', kind: 'prose', provenance: 'observed', required: true, allowsNotes: true },
    { key: 'past_medical_history', label: 'Past Medical History', kind: 'prose', provenance: 'observed', required: true, allowsNotes: true },
    { key: 'social_history', label: 'Social History', kind: 'prose', provenance: 'observed', required: true, allowsNotes: true },
    { key: 'occupational_history', label: 'Occupational History', kind: 'prose', provenance: 'observed', required: true, allowsNotes: true },
    {
        key: 'clinical_examination',
        label: 'Clinical Examination',
        kind: 'prose',
        provenance: 'observed',
        required: true,
        allowsNotes: true,
        guidance: 'Range of motion tables and scored instruments belong here. Check the side of the body.',
    },
    {
        key: 'investigations',
        label: 'Investigations',
        kind: 'prose',
        provenance: 'observed',
        required: true,
        allowsNotes: true,
        guidance: 'List in chronological order. Name the modality, the body part and the date, then the reported finding.',
    },
    { key: 'assessment', label: 'Assessment', kind: 'prose', provenance: 'observed', required: true, allowsNotes: true },
    {
        key: 'questions',
        label: 'Questions',
        kind: 'questions',
        provenance: 'observed',
        required: true,
        allowsNotes: true,
        guidance: 'The questions come from the letter of instruction and are reproduced verbatim, ' +
            'keeping the letter\'s own numbering. A question with parts a to g is a heading ' +
            'and seven answer boxes, never one box. QA checks each question is answered fully and clearly.',
    },
    { key: 'closing', label: 'Closing', kind: 'boilerplate', provenance: 'observed', required: true, boilerplate: CLOSING_TEXT },
    {
        key: 'signature_block',
        label: 'Signature block',
        kind: 'boilerplate',
        provenance: 'observed',
        required: true,
        guidance: 'The signature is already present in the doctor\'s own template. This system ' +
            'produces the document to be signed and does not apply a signature.',
    },
    { key: 'standard_qualifications', label: 'Standard qualifications', kind: 'boilerplate', provenance: 'observed', required: true },
    { key: 'schedule_of_documents', label: 'Schedule of Documents', kind: 'group', provenance: 'observed', required: true },
    { key: 'schedule_referral_letter', label: 'Referral Letter', kind: 'list', provenance: 'observed', parent: 'schedule_of_documents' },
    { key: 'schedule_imaging', label: 'Imaging', kind: 'list', provenance: 'observed', parent: 'schedule_of_documents' },
    { key: 'schedule_specialists', label: 'Specialists', kind: 'list', provenance: 'observed', parent: 'schedule_of_documents' },
    { key: 'schedule_work_certificates', label: 'Work Certificates', kind: 'list', provenance: 'observed', parent: 'schedule_of_documents' },
    { key: 'schedule_physiotherapy', label: 'Physiotherapy', kind: 'list', provenance: 'observed', parent: 'schedule_of_documents' },
    { key: 'schedule_rehabilitation', label: 'Rehabilitation', kind: 'list', provenance: 'observed', parent: 'schedule_of_documents' },
    { key: 'schedule_clinical_notes', label: 'Clinical Notes', kind: 'list', provenance: 'derived', parent: 'schedule_of_documents' },
    { key: 'schedule_other', label: 'Other', kind: 'list', provenance: 'observed', parent: 'schedule_of_documents' },
];
/**
 * Sections observed across the doctor overlays that are not in the house base.
 * These are the pool a doctor's own toggles draw from, so that adding one is a
 * switch rather than a new template.
 */
export const OPTIONAL_SECTIONS = [
    { key: 'family_history', label: 'Family History', kind: 'prose', provenance: 'derived', allowsNotes: true },
    { key: 'general_practitioners', label: 'General Practitioners', kind: 'prose', provenance: 'derived', allowsNotes: true },
    { key: 'clinical_notes_review', label: 'General Practitioners / Clinical Notes', kind: 'prose', provenance: 'derived', allowsNotes: true },
    { key: 'review_of_file_records', label: 'Review of File Records', kind: 'prose', provenance: 'derived', allowsNotes: true },
    { key: 'description_of_injury', label: 'Description of Injury', kind: 'prose', provenance: 'derived', allowsNotes: true },
    { key: 'medication', label: 'Medication', kind: 'prose', provenance: 'derived', allowsNotes: true },
    { key: 'treatment', label: 'Treatment', kind: 'prose', provenance: 'derived', allowsNotes: true },
    { key: 'current_status', label: 'Current Status', kind: 'prose', provenance: 'derived', allowsNotes: true },
    { key: 'executive_summary', label: 'Executive Summary', kind: 'prose', provenance: 'derived', allowsNotes: true },
    { key: 'summary', label: 'Summary', kind: 'prose', provenance: 'derived', allowsNotes: true },
    { key: 'opinion', label: 'Opinion', kind: 'prose', provenance: 'derived', allowsNotes: true },
    { key: 'glossary_of_terms', label: 'Glossary of Terms', kind: 'list', provenance: 'derived' },
    { key: 'supporting_documentation', label: 'Supporting Documentation', kind: 'list', provenance: 'derived' },
    { key: 'mobility_adls', label: 'Current Mobility, Activity and ADLs', kind: 'prose', provenance: 'derived', allowsNotes: true },
    { key: 'other', label: 'Other', kind: 'prose', provenance: 'derived', allowsNotes: true },
];
/**
 * Psychiatry. Derived from the four psychiatrists and the two who write
 * psychiatric reports: Barker, Cocks, Griffiths, Hercus, plus Petch's forms.
 * The physical examination spine is largely absent and a different history
 * spine takes its place.
 */
export const PSYCHIATRY_PACK = {
    key: 'psychiatry',
    label: 'Psychiatry',
    provenance: 'derived',
    note: 'Derived from the overlay diff across Barker, Cocks, Griffiths and Hercus.',
    removes: [
        'clinical_examination',
        'investigations',
        'schedule_imaging',
        'schedule_physiotherapy',
        'schedule_rehabilitation',
    ],
    adds: [
        { key: 'confidential_header', label: 'Confidential Psychiatric Report', kind: 'boilerplate', provenance: 'derived' },
        { key: 'confidentiality_statement', label: 'Confidentiality Statement', kind: 'boilerplate', provenance: 'derived' },
        { key: 'qualification', label: 'Qualification', kind: 'prose', provenance: 'derived' },
        { key: 'background', label: 'Background', kind: 'prose', provenance: 'derived', allowsNotes: true },
        { key: 'personal_history', label: 'Personal History', kind: 'prose', provenance: 'derived', allowsNotes: true },
        { key: 'education', label: 'Education', kind: 'prose', provenance: 'derived', allowsNotes: true },
        { key: 'employment_history', label: 'Employment History', kind: 'prose', provenance: 'derived', allowsNotes: true },
        { key: 'family_psychiatric_history', label: 'Family Psychiatric History', kind: 'prose', provenance: 'derived', allowsNotes: true },
        { key: 'previous_psychiatric_history', label: 'Previous Psychiatric History', kind: 'prose', provenance: 'derived', allowsNotes: true },
        { key: 'substance_use_history', label: 'Substance Use History', kind: 'prose', provenance: 'derived', allowsNotes: true },
        { key: 'history_since_injury', label: 'History Since Injury', kind: 'prose', provenance: 'derived', allowsNotes: true },
        { key: 'treatment_since_injury', label: 'Treatment Since Injury', kind: 'prose', provenance: 'derived', allowsNotes: true },
        { key: 'current_medications', label: 'Current Medications', kind: 'prose', provenance: 'derived', allowsNotes: true },
        { key: 'other_stressors', label: 'Other Stressors', kind: 'prose', provenance: 'derived', allowsNotes: true },
        { key: 'social_supports', label: 'Social Supports', kind: 'prose', provenance: 'derived', allowsNotes: true },
        { key: 'activities_of_daily_living', label: 'Activities of Daily Living', kind: 'prose', provenance: 'derived', allowsNotes: true },
        { key: 'physical_health', label: 'Physical Health', kind: 'prose', provenance: 'derived', allowsNotes: true },
        {
            key: 'mental_state_examination',
            label: 'Mental State Examination',
            kind: 'prose',
            provenance: 'derived',
            allowsNotes: true,
            guidance: 'Appearance, behaviour, speech, mood, affect, thought form and content, perception, cognition, insight, judgement, risk.',
        },
    ],
};
/**
 * Neuropsychology. Collinson's template shares almost nothing with the IME
 * spine, which is why the decision register records it as its own report type
 * rather than an overlay. It is modelled as a pack so the machinery is the
 * same, and marked open because one template is not a pattern.
 */
export const NEUROPSYCHOLOGY_PACK = {
    key: 'neuropsychology',
    label: 'Neuropsychology',
    provenance: 'open',
    note: 'Built from a single template. The decision register has this as its own report type, ' +
        'not an overlay. Treat as a starting point, not a settled spine.',
    removes: [
        'clinical_examination',
        'progress',
        'current_activities',
        'current_medical_treatment',
        'past_medical_history',
        'schedule_physiotherapy',
        'schedule_rehabilitation',
    ],
    adds: [
        { key: 'history_of_injury', label: 'History of Injury', kind: 'prose', provenance: 'derived', allowsNotes: true },
        { key: 'pre_injury_medical_history', label: 'Pre-Injury Medical History', kind: 'prose', provenance: 'derived', allowsNotes: true },
        { key: 'health_and_disability', label: 'Health and Disability', kind: 'prose', provenance: 'derived', allowsNotes: true },
        { key: 'mental_state', label: 'Mental State', kind: 'prose', provenance: 'derived', allowsNotes: true },
        {
            key: 'test_results',
            label: 'Test Results',
            kind: 'prose',
            provenance: 'derived',
            allowsNotes: true,
            guidance: 'Australian-normed instruments. Name the edition and the norms used.',
        },
        { key: 'summary', label: 'Summary', kind: 'prose', provenance: 'derived', allowsNotes: true },
    ],
};
export const SPECIALTY_PACKS = {
    psychiatry: PSYCHIATRY_PACK,
    neuropsychology: NEUROPSYCHOLOGY_PACK,
};
/**
 * The other internal families.
 *
 * Only the IME base exists as a native document in the library. Fitness for
 * work is evidenced by a sample question set, and file review and supplementary
 * are evidenced by filenames alone. Their spines are therefore reductions of
 * the IME spine, marked `derived`, and the sections nothing evidences are
 * absent rather than invented.
 */
export const FAMILY_SPINES = {
    ime: {
        keys: IME_SPINE.map((s) => s.key),
        provenance: 'observed',
        note: 'Section order of the QA training version of the house IME template.',
    },
    ffw: {
        keys: [
            'matter_header', 'opening', 'purpose', 'accompanying_documentation', 'declaration',
            'history', 'current_symptoms', 'current_medical_treatment', 'occupational_history',
            'clinical_examination', 'assessment', 'questions', 'closing', 'signature_block',
            'standard_qualifications', 'schedule_of_documents', 'schedule_referral_letter',
            'schedule_clinical_notes', 'schedule_work_certificates', 'schedule_other',
        ],
        provenance: 'derived',
        note: 'Reduction of the IME spine. The library holds a sample question set for fitness for work ' +
            'but no native template, so this is a starting point to be checked against a real one.',
    },
    'file-review': {
        keys: [
            'matter_header', 'opening', 'purpose', 'accompanying_documentation', 'declaration',
            'review_of_file_records', 'assessment', 'questions', 'closing', 'signature_block',
            'standard_qualifications', 'schedule_of_documents', 'schedule_referral_letter',
            'schedule_specialists', 'schedule_imaging', 'schedule_clinical_notes', 'schedule_other',
        ],
        provenance: 'derived',
        note: 'No examination sections, because there is no examination. Seven doctors hold a file review ' +
            'template; none has been read as a native document yet.',
    },
    supplementary: {
        keys: [
            'matter_header', 'opening', 'purpose', 'accompanying_documentation',
            'questions', 'closing', 'signature_block', 'standard_qualifications',
        ],
        provenance: 'derived',
        note: 'A supplementary answers further questions about an existing report. It carries the question ' +
            'set and little else. Billed at the hourly rate.',
    },
};
export function sectionIndex(spine) {
    return new Map(spine.map((s) => [s.key, s]));
}
export const ALL_SECTIONS = new Map([
    ...IME_SPINE,
    ...OPTIONAL_SECTIONS,
    ...Object.values(SPECIALTY_PACKS).flatMap((p) => p.adds),
].map((s) => [s.key, s]));
//# sourceMappingURL=sections.js.map